import { useCallback, useMemo, useState } from "react";
import { t } from "@lingui/macro";
import { useHeaderContext } from "@WahooFitness/wahoo-offline-mfe";
import useTieredMembership from "@/components/Membership/useTieredMembership";
import usePlanPurchase, { didPurchaseSucceed } from "@/components/Membership/usePlanPurchase";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import {
  ClickTarget,
  EventType,
  PLAN_CLICK_TARGET,
  SourcePage,
} from "@/services/Analytics/EventTypes";
import { Plan } from "@WahooFitness/redesignr";

function useChangePlan() {
  const { navigateBack } = useHeaderContext();
  const {
    activeTier,
    hasTrialNoPendingSub,
    proPkg,
    corePkg,
    offeringsLoading,
    offeringsError,
    userSubscription,
    customerInfo,
  } = useTieredMembership();

  const { purchasePlan, purchaseInProgress } = usePlanPurchase();
  const { logEvent } = useLogEvent();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const hasProSubscription = activeTier === "pro" && !hasTrialNoPendingSub;

  const effectiveSelectedPlan: Plan = selectedPlan ?? (hasProSubscription ? "core" : "pro");

  const coreTrialAvailable = activeTier !== "core" && !!corePkg?.product.trialAvailable;

  const onPlanSelect = useCallback(
    (plan: Plan) => {
      setSelectedPlan(plan);
      logEvent("change_plan_tier_selected", {
        event_type: EventType.click,
        click_target: PLAN_CLICK_TARGET[plan],
        source_page_name: SourcePage.change_plan,
      });
    },
    [logEvent]
  );

  const isUpgrade =
    effectiveSelectedPlan === "pro" || (effectiveSelectedPlan === "core" && activeTier === "base");

  const buttonLabel = useMemo<string | null>(() => {
    if (!activeTier || (effectiveSelectedPlan === activeTier && !hasTrialNoPendingSub)) return null;
    if (effectiveSelectedPlan === "pro") return t`Upgrade to Pro`;
    if (effectiveSelectedPlan === "core") {
      return activeTier === "base" ? t`Upgrade to Core` : t`Downgrade to Core`;
    }
    return t`Downgrade to Base`;
  }, [activeTier, effectiveSelectedPlan, hasTrialNoPendingSub]);

  const onChangePlan = useCallback(async () => {
    logEvent("change_plan_cta", {
      event_type: EventType.click,
      click_target: isUpgrade ? ClickTarget.paywall_upgrade : ClickTarget.downgrade,
      source_page_name: SourcePage.change_plan,
    });
    const result = await purchasePlan(effectiveSelectedPlan);
    if (didPurchaseSucceed(result)) {
      navigateBack();
    }
    // TODO: show error toast for failures
  }, [logEvent, isUpgrade, purchasePlan, effectiveSelectedPlan, navigateBack]);

  return {
    activeTier,
    hasTrialNoPendingSub,
    selectedPlan: effectiveSelectedPlan,
    onPlanSelect,
    coreTrialAvailable,
    buttonLabel,
    proPkg,
    corePkg,
    offeringsLoading,
    offeringsError,
    onChangePlan,
    purchaseInProgress,
    userSubscription,
    customerInfo,
  };
}

export default useChangePlan;
